import { Table } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import PaginationComponent from './PaginationComponent';
import './_table.scss';

interface T {
  limit: number;
  offset: number;
}

interface Props {
  columns: Array<object>;
  dataSource: Array<object>;
  currentPageHandler?: (data: T) => void;
  getCheckList?: any;
  count?: number;
  loading?: boolean;
  urlOffset?: number;
  showPagination?: boolean;
  urlLimit?: number;
  locale?: any;
  setRowKey?: string;
  expandable?: any;
  type?: 'default' | 'custom';
  expandableKey?: any;
  className?: string;
  rowClassName?: any;
  customExpandIcon?: boolean;
  expandedRowKeys?: any[];
  onExpandedRowsChange?: (keys: any[]) => void;
  scroll?: any;
  showHeader?: boolean;
  isModern?: boolean;
  disableParentChildHierarchy?: boolean;
  showExpandColumn?: boolean;
  fixedRowSelection?: boolean;
  stickySelection?: boolean;
}

const TableComponent: React.FC<Props> = ({
  columns,
  dataSource,
  currentPageHandler,
  getCheckList,
  count,
  loading,
  urlOffset,
  showPagination,
  urlLimit,
  locale,
  setRowKey,
  expandableKey,
  type,
  expandable,
  className,
  rowClassName,
  customExpandIcon,
  expandedRowKeys,
  onExpandedRowsChange,
  scroll,
  showHeader,
  isModern,
  disableParentChildHierarchy,
  showExpandColumn = true,
  fixedRowSelection = false,
  stickySelection = false
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]),
    rowKey = (record: any) => {
      switch (setRowKey) {
        case 'id':
          return record.id;

        case 'index':
          return dataSource.indexOf(record);

        case 'uuid':
          return record?.uuid;

        default:
          return dataSource.indexOf(record);
      }
    },
    rowSelection = {
      fixed: fixedRowSelection,
      selectedRowKeys: selectedRowKeys,
      onChange: (selectedRowKeys: any) => {
        setSelectedRowKeys(selectedRowKeys);
      },
      onSelectNone: () => {
        setSelectedRowKeys([]);
      },
      ...(stickySelection && { fixed: true }),
    };

  useEffect(() => {
    rowSelection.onSelectNone();
  }, [dataSource]);

  useEffect(() => {
    rowSelection.onSelectNone();
  }, [dataSource]);

  useEffect(() => {
    if (getCheckList) {
      getCheckList(selectedRowKeys);
    }
  }, [selectedRowKeys]);

  return (
    <div className={`table-component ${isModern ? 'modern-table' : ''}`}>
      <Table
        className={`table ${className}`}
        columns={columns}
        dataSource={dataSource}
        {...(rowKey && { rowKey: (record) => rowKey(record) })}
        {...(getCheckList && { rowSelection: { ...rowSelection } })}
        {...(type !== 'default' && { pagination: false })}
        {...(loading && { loading: loading })}
        {...(locale && { locale: locale })}
        {...(rowClassName && { rowClassName: rowClassName })}
        {...(scroll && { scroll: scroll })}
        {...(showHeader !== undefined && { showHeader: showHeader })}
        {...((expandable || (isModern && expandableKey && dataSource?.some((r: any) => r?.[expandableKey]?.length))) && {
          expandable: {
            ...(expandable && { expandedRowRender: (record) => expandable(record) }),
            rowExpandable: (record) => record?.[expandableKey]?.length,
            childrenColumnName: disableParentChildHierarchy ? 'non_existent_key' : expandableKey,
            expandedRowKeys,
            onExpandedRowsChange,
            showExpandColumn,
            expandIcon: ({ expanded, onExpand, record }) => {
              if (record?.[expandableKey]?.length) {
                if (customExpandIcon) {
                  return (
                    <span
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...(isModern ? { width: '24px', height: '24px', marginRight: '10px' } : {})
                      }}
                    >
                      {expanded ?
                        <DownOutlined style={{ fontSize: isModern ? '12px' : '10px' }} /> :
                        <RightOutlined style={{ fontSize: isModern ? '12px' : '10px' }} />
                      }
                    </span>
                  )
                } else {
                  if (expanded) {
                    return (
                      <div className="cta" onClick={(e) => onExpand(record, e)}>
                        Hide
                      </div>
                    );
                  } else {
                    return (
                      <div className="cta" onClick={(e) => onExpand(record, e)}>
                        View
                      </div>
                    );
                  }
                }
              }
            },
          },
        })}
      />
      {(!type || type !== 'default') && showPagination && (
        <PaginationComponent
          total={count}
          onPageChange={currentPageHandler}
          urlOffset={urlOffset}
          urlLimit={urlLimit}
        />
      )}
    </div>
  );
};

export default TableComponent;
